<template>
    <div class="address-card">
        <div class="address-card__header">
            <h3 class="typographies-heading-h3">{{ address.name }}</h3>
            <div class="address-card__actions-container">
                <CompassTooltip
                    :content="textTooltipDelete"
                    :disabled="canDeleteAddress"
                    :arrow="false"
                    placement="bottom-end"
                >
                    <CompassIconButton
                        icon="bin"
                        variant="destructive"
                        :disabled="disableDeleteButton"
                        @click="$emit('onDelete')"
                    />
                </CompassTooltip>
                <CompassTooltip
                    :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                    :disabled="permissions.can_edit_addresses"
                    :arrow="false"
                    placement="bottom-end"
                >
                    <CompassIconButton
                        icon="edit"
                        :disabled="disableEditButton"
                        @click="$emit('onEdit', address)"
                    />
                </CompassTooltip>
            </div>
        </div>
        <div class="typographies-paragraph-medium-regular">
            <p>{{ address.address_line_1 }}</p>
            <p v-if="address.address_line_2">{{ address.address_line_2 }}</p>
            <p v-if="address.address_line_3">{{ address.address_line_3 }}</p>
            <p v-if="address.address_line_4">{{ address.address_line_4 }}</p>
            <p>{{ address.city }}, {{ address.postal_code }}</p>
            <p>
                {{ address.country }}
            </p>
        </div>
        <div class="address-card__switch-container">
            <CompassTooltip
                :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                :arrow="false"
                :disabled="permissions.can_edit_addresses"
            >
                <CompassSwitch
                    :value="localInvoicingAddressSwitch"
                    :disabled="isSwitchInvoicingDisabled"
                    :label="labelSwitchInvoicing"
                    orientation="left"
                    @change="handleInvoicingAddressSwitch"
                />
                <CompassLoader v-if="isSwitchInvoicingLoading" size="small" />
            </CompassTooltip>
            <CompassTooltip
                :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                :arrow="false"
                :disabled="permissions.can_edit_addresses"
            >
                <CompassSwitch
                    :value="localDeliveryAddressSwitch"
                    :disabled="isSwitchDeliveryDisabled"
                    :label="labelSwitchDelivery"
                    orientation="left"
                    @change="handleDeliveryAddressSwitch"
                />
                <CompassLoader v-if="isSwitchDeliveryLoading" size="small" />
            </CompassTooltip>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        address: {
            type: Object,
            required: true,
        },
        permissions: {
            type: Object,
            required: true,
        },
        context: {
            type: String,
            default: 'overview',
        },
        addressUpdating: {
            type: [Number, null],
            default: null,
        },
        disableSwitches: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localDeliveryAddressSwitch: this.address.is_delivery_address,
            localInvoicingAddressSwitch: this.address.is_invoicing_address,
        };
    },
    computed: {
        textTooltipDelete() {
            if (!this.permissions.can_delete_addresses) {
                return this.$t.__('common.labels.no_permissions_to_perform_this_action');
            } else if (this.address.is_invoicing_address || this.address.is_delivery_address) {
                return this.$t.__('common.address.AddressCard.tooltip_default_address');
            }
            return '';
        },
        canDeleteAddress() {
            return (
                this.permissions.can_delete_addresses &&
                !this.address.is_invoicing_address &&
                !this.address.is_delivery_address
            );
        },
        isSwitchDeliveryDisabled() {
            if (!this.permissions.can_edit_addresses) {
                return true;
            }
            return (
                this.localDeliveryAddressSwitch ||
                this.isSwitchDeliveryLoading ||
                this.disableSwitches
            );
        },
        isSwitchDeliveryLoading() {
            return (
                this.addressUpdating === this.address.id &&
                this.localDeliveryAddressSwitch !== this.address.is_delivery_address
            );
        },
        isSwitchInvoicingDisabled() {
            if (!this.permissions.can_edit_addresses) {
                return true;
            }
            return (
                this.localInvoicingAddressSwitch ||
                this.isSwitchInvoicingLoading ||
                this.disableSwitches
            );
        },
        isSwitchInvoicingLoading() {
            return (
                this.addressUpdating === this.address.id &&
                this.localInvoicingAddressSwitch !== this.address.is_invoicing_address
            );
        },
        labelSwitchDelivery() {
            return this.context === 'overview'
                ? this.$t.__('common.address.AddressCard.switch_delivery_address_overview')
                : this.$t.__('common.address.AddressCard.switch_delivery_address');
        },
        labelSwitchInvoicing() {
            return this.context === 'overview'
                ? this.$t.__('common.address.AddressCard.switch_invoicing_address_overview')
                : this.$t.__('common.address.AddressCard.switch_invoicing_address');
        },
        disableDeleteButton() {
            return !this.canDeleteAddress || !!this.addressUpdating || this.disableSwitches;
        },
        disableEditButton() {
            return (
                !this.permissions.can_edit_addresses ||
                !!this.addressUpdating ||
                this.disableSwitches
            );
        },
    },
    watch: {
        'address.is_delivery_address': {
            handler(newIsDeliveryAddress) {
                this.localDeliveryAddressSwitch = newIsDeliveryAddress;
            },
        },
        'address.is_invoicing_address': {
            handler(newIsInvoicingAddress) {
                this.localInvoicingAddressSwitch = newIsInvoicingAddress;
            },
        },
        addressUpdating(newValue) {
            if (newValue === null) {
                this.localDeliveryAddressSwitch = this.address.is_delivery_address;
                this.localInvoicingAddressSwitch = this.address.is_invoicing_address;
            }
        },
    },
    methods: {
        handleDeliveryAddressSwitch(value) {
            if (this.addressUpdating) {
                return;
            }
            this.localDeliveryAddressSwitch = value;
            this.$emit('onDeliveryAddressSwitch');
        },
        handleInvoicingAddressSwitch(value) {
            if (this.addressUpdating) {
                return;
            }
            this.localInvoicingAddressSwitch = value;
            this.$emit('onInvoicingAddressSwitch');
        },
    },
};
</script>

<style lang="scss" scoped>
.address-card {
    background-color: var(--colors-common-background-default);
    border-radius: var(--spacing-0);
    box-shadow: var(--elevation-1);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
    padding: var(--spacing-3);

    p,
    h3 {
        margin: 0;
    }

    &__header {
        align-items: center;
        display: flex;
        justify-content: space-between;
    }

    &__switch-container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-0);

        .compass-switch::v-deep > label {
            justify-content: flex-end;
        }

        .compass-tooltip::v-deep {
            .compass-tooltip__element {
                display: inline-flex;
                gap: var(--spacing-1);
            }
        }
    }
}
</style>
